import { Box, Disclaimer, Div, lightTheme } from '@vgrubs/components';
import { ClientPaymentEntity } from 'components/src/types/clientPayments.types';
import {
  PhoneCardDetails,
  PhoneCardLabel,
} from '../../Receivables/components/PhoneCardDetails';
import {
  LocationLink,
  OwnerLink,
  PaymentAmount,
  PaymentStatusPill,
  RetryPaymentButton,
  canRetryPayment,
  clientPaymentsColumns,
  formatPaymentDate,
} from './ClientPaymentCells';

/**
 * One client payment: columns under the list's TableHeader on desktop, a
 * card on phones with the amount top right, across from the owner.
 */
export const ClientPaymentComponent = ({
  payment,
  retryPayment,
  columns,
}: {
  payment: ClientPaymentEntity;
  retryPayment: (id: string | number) => Promise<void>;
  /** The list's clientPaymentsColumns, so the row matches its header. */
  columns: ReturnType<typeof clientPaymentsColumns>;
}) => {
  const retry = canRetryPayment(payment)
    ? () => retryPayment(payment.id)
    : null;
  const [id, date, owner, location, status, amount, action] = columns;

  return (
    <Box
      $mb="8"
      $flex
      $alignCenter
      $gap="20px"
      $minHeight="64px"
      $p="12px 24px"
    >
      <Div $desktop $width={id.width}>
        <Disclaimer $m="0">#{payment.id}</Disclaimer>
      </Div>
      <Div $desktop $width={date.width}>
        <Disclaimer $m="0">{formatPaymentDate(payment)}</Disclaimer>
      </Div>
      <Div $desktop $width={owner.width} $minWidth="0">
        <OwnerLink payment={payment} />
      </Div>
      <Div $desktop $width={location.width} $minWidth="0">
        <LocationLink payment={payment} />
      </Div>
      <Div $desktop $width={status.width}>
        <Div $flex>
          <PaymentStatusPill status={payment.status} />
        </Div>
      </Div>
      <Div $desktop $width={amount.width}>
        <PaymentAmount payment={payment} />
      </Div>
      {action && (
        <Div $desktop $width={action.width}>
          {retry && <RetryPaymentButton onClick={retry} />}
        </Div>
      )}

      <Div $mobile $width="100%" $minWidth="0">
        <Div $flex $alignStart $between $gap="16px">
          <Div $flex $column $gap="4px" $minWidth="0">
            <OwnerLink payment={payment} title />
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              #{payment.id} · {formatPaymentDate(payment)}
            </Disclaimer>
          </Div>
          {/* Never shrinks: the name is the part that gives way. */}
          <Div $flex $column $alignEnd $gap="6px" style={{ flex: 'none' }}>
            <PaymentAmount payment={payment} />
            <PaymentStatusPill status={payment.status} />
          </Div>
        </Div>

        <PhoneCardDetails>
          <PhoneCardLabel>Location</PhoneCardLabel>
          <LocationLink payment={payment} />
        </PhoneCardDetails>

        {retry && (
          <Div $mt="16">
            <RetryPaymentButton onClick={retry} />
          </Div>
        )}
      </Div>
    </Box>
  );
};
